import React, { useState, useEffect, useMemo } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useToast } from '@/components/ui/use-toast'
import { cn } from '@/lib/utils'
import { useParams } from 'react-router-dom'
import useSWR, { mutate } from 'swr'
import { fetcher, putFetcher } from 'api/swr'
import Loader from '@/components/ui/Loader'
import { Pencil1Icon } from '@radix-ui/react-icons'

const BindLayersDialog = ({ toning }) => {
  const { tid } = useParams()
  const { toast } = useToast()
  const [open, setOpen] = useState(false)
  const [selectedLayers, setSelectedLayers] = useState(new Set())

  const { data: layers, isLoading: layersLoading } = useSWR(
    ['/layers', { tid }],
    fetcher,
  )

  // 初始化选中的 layers（当前绑定到此 toning 的 layers）
  useEffect(() => {
    if (layers && toning) {
      const boundLayerIds = new Set(
        layers
          .filter((layer) => layer.toning === toning.id)
          .map((layer) => layer.id),
      )
      setSelectedLayers(boundLayerIds)
    }
  }, [layers, toning, open])

  const [isMutating, setIsMutating] = useState(false)

  const handleToggleLayer = (layerId) => {
    setSelectedLayers((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(layerId)) {
        newSet.delete(layerId)
      } else {
        newSet.add(layerId)
      }
      return newSet
    })
  }

  const updateLayerToning = async (layerId, toningId) => {
    return putFetcher(
      `/layers/${layerId}`,
      {
        arg: {
          tid,
          key: 'toning',
          value: toningId,
        },
      },
    )
  }

  const handleSubmit = async () => {
    if (!layers) return

    setIsMutating(true)
    try {
      // 找出需要更新的 layers
      const toBind = Array.from(selectedLayers)
      const toUnbind = Array.from(
        layers
          .filter(
            (layer) =>
              layer.toning === toning.id && !selectedLayers.has(layer.id),
          )
          .map((layer) => layer.id),
      )

      // 批量更新：先解绑，再绑定
      const updatePromises = []

      // 解绑不再选中的 layers
      for (const layerId of toUnbind) {
        updatePromises.push(updateLayerToning(layerId, null))
      }

      // 绑定新选中的 layers（包括从其他 toning 转移过来的）
      for (const layerId of toBind) {
        const layer = layers.find((l) => l.id === layerId)
        if (layer && layer.toning !== toning.id) {
          // 即使已经绑定到其他 toning，也直接更新，API 会处理
          updatePromises.push(updateLayerToning(layerId, toning.id))
        }
      }

      await Promise.all(updatePromises)

      // 刷新数据
      mutate(['/layers', { tid }])
      mutate(['/tonings', { tid }])

      toast({
        title: 'Layers Updated',
        description: `Successfully updated ${updatePromises.length} layer(s).`,
        className: 'bg-green-500 text-white',
      })

      setOpen(false)
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Update Failed',
        description: error.message || 'Failed to update layers.',
      })
    } finally {
      setIsMutating(false)
    }
  }

  const hasChanges = useMemo(() => {
    if (!layers) return false
    const currentBound = new Set(
      layers
        .filter((layer) => layer.toning === toning.id)
        .map((layer) => layer.id),
    )
    if (currentBound.size !== selectedLayers.size) return true
    for (const id of currentBound) {
      if (!selectedLayers.has(id)) return true
    }
    for (const id of selectedLayers) {
      if (!currentBound.has(id)) return true
    }
    return false
  }, [layers, toning, selectedLayers])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-6 w-6 p-0 hover:bg-muted"
          onClick={(e) => {
            e.stopPropagation()
          }}
        >
          <Pencil1Icon className="h-3 w-3" />
          <span className="sr-only">Bind layers</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-[600px] max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Bind Layers to Toning</DialogTitle>
          <DialogDescription>
            Select layers to bind to "{toning.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="py-4">
          {layersLoading ? (
            <Loader>Loading layers...</Loader>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {layers?.length === 0 ? (
                  <div className="text-sm text-muted-foreground text-center py-8">
                    No layers available
                  </div>
                ) : (
                  layers?.map((layer) => {
                    const isSelected = selectedLayers.has(layer.id)
                    const isBoundToOtherToning =
                      layer.toning && layer.toning !== toning.id

                    return (
                      <div
                        key={layer.id}
                        className={cn(
                          'flex items-center space-x-2 p-2 rounded-md border',
                          isBoundToOtherToning && 'bg-muted/50',
                        )}
                      >
                        <Checkbox
                          id={`layer-${layer.id}`}
                          checked={isSelected}
                          onCheckedChange={() => handleToggleLayer(layer.id)}
                          disabled={isMutating}
                        />
                        <Label
                          htmlFor={`layer-${layer.id}`}
                          className={cn(
                            'flex-1 cursor-pointer text-sm',
                            isBoundToOtherToning && 'text-muted-foreground',
                          )}
                        >
                          <div className="flex items-center justify-between">
                            <span>{layer.name || layer.id}</span>
                            {isBoundToOtherToning && (
                              <span className="text-xs text-muted-foreground">
                                (bound to another toning)
                              </span>
                            )}
                          </div>
                        </Label>
                      </div>
                    )
                  })
                )}
              </div>
            </ScrollArea>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!hasChanges || isMutating || layersLoading}
          >
            {isMutating ? (
              <>
                <Loader className="mr-2 h-4 w-4" />
                Updating...
              </>
            ) : (
              'Save Changes'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default BindLayersDialog

